"use client";

import { ProductsAndServicesPageQueryResult } from "@/sanity.types";
import { urlFor } from "@/sanity/lib/image";
import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import BadgeRoundedSquare from "@/app/(site)/_components/utils/badge-rounded-square/BadgeRoundedSquare";
import { useMediaQuery } from "@/app/(site)/_hooks/useMediaQuery";
import clsx from "clsx";

type HowWeWorkSliderProps = {
	steps: NonNullable<ProductsAndServicesPageQueryResult>["howWeWork"]["steps"];
};

const SmallSquare = () => <div className="bg-olive-green h-1.5 w-1.5 rounded-[0.063rem]" />;

export default function HowWeWorkSlider({ steps }: HowWeWorkSliderProps) {
	const [activeSlide, setActiveSlide] = useState(0);
	const [isAnimating, setIsAnimating] = useState(false);
	const sectionRef = useRef<HTMLDivElement | null>(null);
	const isDesktop = useMediaQuery("(min-width: 990px)");

	useEffect(() => {
		const handleScroll = () => {
			if (!sectionRef.current) return;

			// Find parent section with height
			const section = sectionRef.current.closest('section[id="howWeWork"]') as HTMLElement;
			if (!section) return;

			const sectionRect = section.getBoundingClientRect();
			const sectionTop = sectionRect.top;
			const sectionHeight = sectionRect.height;
			const viewportHeight = window.innerHeight;

			// Calculate progress in the section
			const scrollProgress = Math.max(0, Math.min(1, -sectionTop / (sectionHeight - viewportHeight)));

			// Calculate which slide should be active
			const slideIndex = Math.floor(scrollProgress * steps.length);
			const clampedIndex = Math.max(0, Math.min(steps.length - 1, slideIndex));

			if (clampedIndex !== activeSlide) {
				setIsAnimating(true);
				setActiveSlide(clampedIndex);
				setTimeout(() => setIsAnimating(false), 400);
			}
		};

		window.addEventListener("scroll", handleScroll);
		handleScroll();

		return () => {
			window.removeEventListener("scroll", handleScroll);
		};
	}, [activeSlide, steps.length]);

	const handleSlideClick = (index: number) => {
		if (index === activeSlide) return;

		setIsAnimating(true);
		setTimeout(() => setIsAnimating(false), 400);

		const section = sectionRef.current?.closest('section[id="howWeWork"]') as HTMLElement;
		if (section) {
			const sectionRect = section.getBoundingClientRect();
			const sectionTop = sectionRect.top + window.scrollY;
			const viewportHeight = window.innerHeight;

			const targetScroll = sectionTop + (index / steps.length) * (sectionRect.height - viewportHeight);

			window.scrollTo({
				top: targetScroll,
				behavior: "smooth",
			});
		}
	};

	const currentStep = steps[activeSlide];

	if (!currentStep) return null;

	if (!isDesktop) {
		return (
			<div ref={sectionRef} className="relative">
				<div className="transition-all duration-400 ease-out">
					<div
						className={clsx(
							"relative aspect-[355/284] overflow-hidden rounded-[0.336rem] transition-all duration-400 ease-out",
							isAnimating ? "scale-95 rotate-1" : "scale-100 rotate-0",
						)}
					>
						<Image
							src={urlFor(currentStep.image).url()}
							alt={currentStep.title}
							fill
							className={clsx(
								"h-full w-full object-cover transition-all duration-600 ease-out",
								isAnimating ? "scale-110 brightness-75" : "scale-100 brightness-100",
							)}
						/>
					</div>

					<div className="xs:mt-5 mt-2.5 flex justify-center gap-3 transition-all delay-75 duration-300 ease-out">
						{steps.map((_, index) => (
							<BadgeRoundedSquare
								key={index}
								text={activeSlide === index ? String(index + 1).padStart(2, "0") : undefined}
								icon={activeSlide !== index ? <SmallSquare /> : undefined}
								backgroundColor={activeSlide === index ? "bg-olive-green-light" : "bg-transparent"}
								textColor={activeSlide === index ? "text-olive-green" : "text-grey-200"}
								className="cursor-pointer transition-all duration-300 hover:scale-110"
								onClick={() => handleSlideClick(index)}
							/>
						))}
					</div>

					<div className="mt-5 text-center transition-all delay-100 duration-350 ease-out">
						<p className="text-h7-mob sm:text-h6 text-graphite-black mb-3.5 transition-all delay-125 duration-300">
							{currentStep.title}
						</p>
						<p className="text-body-m sm:text-body-l text-graphite-black/70 whitespace-pre-wrap transition-all delay-150 duration-300">
							{currentStep.description}
						</p>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div ref={sectionRef} className="relative">
			<div className="grid grid-cols-8 gap-x-5">
				<div className="col-span-3">
					<div
						className={clsx(
							"relative aspect-[355/284] overflow-hidden rounded-[0.336rem] transition-all duration-400 ease-out",
							isAnimating ? "scale-[0.97] rotate-[-1deg]" : "scale-100 rotate-0",
						)}
					>
						<Image
							src={urlFor(currentStep.image).url()}
							alt={currentStep.title}
							fill
							className={clsx(
								"h-full w-full object-cover transition-all duration-500 ease-out",
								isAnimating ? "scale-105 brightness-90" : "scale-100 brightness-100",
							)}
						/>
						<div className="bg-graphite-black/20 absolute inset-0" />
					</div>
				</div>

				<div className="col-span-4 mx-auto flex max-w-[85%] flex-col justify-center">
					<div className="transition-all delay-50 duration-350 ease-out">
						<p className="text-h7-mob sm:text-h6 text-graphite-black mb-3.5 transition-all delay-75 duration-300">
							{currentStep.title}
						</p>
						<p className="text-body-m sm:text-body-l text-graphite-black/70 whitespace-pre-wrap transition-all delay-100 duration-300">
							{currentStep.description}
						</p>
					</div>
				</div>

				<div className="col-span-1 flex flex-col justify-center">
					<div className="flex h-full flex-col justify-between gap-3">
						{steps.map((_, index) => (
							<BadgeRoundedSquare
								key={index}
								text={activeSlide === index ? String(index + 1).padStart(2, "0") : undefined}
								icon={activeSlide !== index ? <SmallSquare /> : undefined}
								backgroundColor={activeSlide === index ? "bg-olive-green-light" : "bg-transparent"}
								textColor={activeSlide === index ? "text-olive-green" : "text-grey-200"}
								className="cursor-pointer transition-all duration-300 hover:scale-110"
								onClick={() => handleSlideClick(index)}
							/>
						))}
					</div>
				</div>
			</div>
		</div>
	);
}
